import axios from 'axios'
import { attachOrgLockedHandler } from '@/services/org-locked'
import store from '@/store'
import { envConfig } from '@/utils/env-config'
import { readToken, clearToken, ACCESS_TOKEN_KEY } from '@/utils/token-storage'

// Create axios instance
const apiClient = axios.create({
  baseURL: envConfig.VUE_APP_API_FULL_URL || envConfig.VITE_API_FULL_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'X-Client-Type': 'vue-website',
    'X-Client-Version': '1.0.0'
  }
})

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available (Vuex first, then storage; readToken checks
    // both localStorage and sessionStorage so Remember-me=off sessions work).
    const token = store?.state?.auth?.accessToken || readToken(ACCESS_TOKEN_KEY)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add request timestamp
    config.headers['X-Request-Time'] = new Date().toISOString()

    // Log request in development
    if (envConfig.VUE_APP_DEBUG || envConfig.VITE_DEBUG) {
      console.log('API Request:', config.method?.toUpperCase(), config.url)
    }

    return config
  },
  (error) => {
    console.error('Request interceptor error:', error)
    return Promise.reject(error)
  }
)

// Self-heal throttle: when an org-scoped request 403s because the user was
// removed from the active org server-side, we nudge /me to re-sync the org
// list (NavBar's reconcile then switches/logs out). Throttled so a burst of
// 403s can't trigger a fetch storm, and never recurses (fetchMe hits the
// auth-api client, not this one).
let lastSelfHealAt = 0
function selfHealMembership() {
  const now = Date.now()
  if (now - lastSelfHealAt < 15000) return
  lastSelfHealAt = now
  try {
    if (store?.state?.auth?.isAuthenticated) {
      store.dispatch('auth/fetchMe')
    }
  } catch (_) { /* never let self-heal break the error path */ }
}

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Log response in development
    if (envConfig.VUE_APP_DEBUG || envConfig.VITE_DEBUG) {
      console.log('API Response:', response.status, response.config.url)
    }

    return response
  },
  (error) => {
    console.error('API Error:', error.response?.status, error.response?.data || error.message)

    // Handle common errors
    if (error.response?.status === 401) {
      // Unauthorized - clear token (refresh token should be handled by auth-api.js)
      clearToken(ACCESS_TOKEN_KEY)
      console.log('Token expired, auth-api.js should handle refresh')
    } else if (error.response?.status === 403) {
      // Forbidden — may be a stale active org (super-admin removed the user).
      // Re-sync /me so NavBar's reconcile corrects the switcher / session.
      console.warn('Access forbidden:', error.response.data)
      selfHealMembership()
    } else if (error.response?.status >= 500) {
      // Server error
      console.error('Server error:', error.response.status)
    }

    return Promise.reject(error)
  }
)

// API service methods
const api = {
  // Authentication endpoints
  async login(credentials) {
    try {
      return await apiClient.post('/auth/login', credentials)
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Login failed')
    }
  },

  async logout(token) {
    try {
      return await apiClient.post('/auth/logout', { token })
    } catch (error) {
      console.warn('Logout API call failed:', error.message)
      // Don't throw error for logout failures
      return { data: { success: true } }
    }
  },

  async verifyToken(token) {
    try {
      return await apiClient.get('/auth/verify', {
        headers: { Authorization: `Bearer ${token}` }
      })
    } catch (error) {
      throw new Error('Token verification failed')
    }
  },

  async refreshToken(refreshToken) {
    try {
      return await apiClient.post('/auth/refresh', { refreshToken })
    } catch (error) {
      throw new Error('Token refresh failed')
    }
  },

  // Platform endpoints
  async getEnvironments() {
    try {
      return await apiClient.get('/environments')
    } catch (error) {
      throw new Error('Failed to fetch environments')
    }
  },

  async createEnvironment(environmentData) {
    try {
      return await apiClient.post('/environments', environmentData)
    } catch (error) {
      throw new Error('Failed to create environment')
    }
  },

  async getEnvironment(id) {
    try {
      return await apiClient.get(`/environments/${id}`)
    } catch (error) {
      throw new Error('Failed to fetch environment')
    }
  },

  async deleteEnvironment(id) {
    try {
      return await apiClient.delete(`/environments/${id}`)
    } catch (error) {
      throw new Error('Failed to delete environment')
    }
  },

  // AI/LLM endpoints
  async getChatCompletion(messages) {
    try {
      return await apiClient.post('/ai/chat', { messages })
    } catch (error) {
      throw new Error('AI chat completion failed')
    }
  },

  async getAvailableModels() {
    try {
      return await apiClient.get('/ai/models')
    } catch (error) {
      throw new Error('Failed to fetch AI models')
    }
  },

  // Contact/Support endpoints
  async submitContactForm(formData) {
    try {
      return await apiClient.post('/contact', formData)
    } catch (error) {
      throw new Error('Failed to submit contact form')
    }
  },

  async requestDemo(demoData) {
    try {
      return await apiClient.post('/demo/request', demoData)
    } catch (error) {
      throw new Error('Failed to request demo')
    }
  },

  // Monitoring/Logging endpoints
  async logSecurityEvent(eventData) {
    try {
      return await apiClient.post('/security/log', eventData)
    } catch (error) {
      console.warn('Failed to log security event:', error.message)
      // Don't throw error for logging failures
    }
  },

  async logError(errorData) {
    try {
      return await apiClient.post('/errors/log', errorData)
    } catch (error) {
      console.warn('Failed to log error:', error.message)
    }
  },

  // Health check
  async healthCheck() {
    try {
      return await apiClient.get('/health')
    } catch (error) {
      throw new Error('Health check failed')
    }
  },

  // Utility method for testing API connectivity
  async testConnection() {
    try {
      const response = await this.healthCheck()
      return {
        connected: true,
        status: response.status,
        data: response.data
      }
    } catch (error) {
      return {
        connected: false,
        error: error.message
      }
    }
  }
}

// Export both the configured axios instance and the API service
export { apiClient }
export default api

// 423 organization_locked → the locked-organisation page, once per burst.
attachOrgLockedHandler(apiClient)
